import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useJixxeoRecorder } from '../../context/RecorderContext';
import { formatBytes, formatDuration, suggestedFilename, renameRecording, deleteRecording } from '../../lib/recordingsDb';
import { Download, Share2, Trash2, Edit3, Check, FileVideo } from 'lucide-react';
import { toast } from 'sonner';

export default function RecordingPreview() {
  const rec = useJixxeoRecorder();
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState('');

  if (!rec || !rec.previewRec) return null;
  const r = rec.previewRec;
  const allowDownloads = rec.settings?.allow_downloads !== false;
  const allowSharing = rec.settings?.allow_sharing !== false;
  const isAudio = r.kind === 'audio' || r.mime_type.startsWith('audio/');
  const ext = r.mime_type.includes('mp4') ? (isAudio ? 'M4A' : 'MP4') : (isAudio ? 'WEBM audio' : 'WEBM');

  const download = () => {
    const a = document.createElement('a');
    a.href = r.url; a.download = suggestedFilename(r);
    document.body.appendChild(a); a.click(); a.remove();
    toast.success('Download started');
  };
  const doShare = async () => {
    try {
      const file = new File([r.blob], suggestedFilename(r), { type: r.mime_type });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: r.title, text: 'My Jixxeo AI recording' });
      } else if (navigator.share) {
        await navigator.share({ title: r.title, text: 'My Jixxeo AI recording' });
      } else {
        download();
        toast.info('Sharing not supported here — download started instead');
      }
    } catch (e) { /* user cancelled */ }
  };
  const doDelete = async () => {
    if (!window.confirm('Delete this recording?')) return;
    await deleteRecording(r.id);
    rec.closePreview();
    toast.success('Recording deleted');
  };
  const saveTitle = async () => {
    await renameRecording(r.id, title.trim() || r.title);
    r.title = title.trim() || r.title;
    setRenaming(false);
    toast.success('Renamed');
  };

  return (
    <Dialog open={!!rec.previewRec} onOpenChange={(o) => { if (!o) rec.closePreview(); }}>
      <DialogContent className="glass border-white/10 max-w-2xl p-0 overflow-hidden" data-testid="recording-preview-dialog">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="font-display flex items-center gap-2">
            <FileVideo className="w-5 h-5 text-violet-400" />
            Your Jixxeo AI Recording
          </DialogTitle>
        </DialogHeader>
        <video
          data-testid="preview-video"
          src={r.url}
          controls
          autoPlay
          className={`w-full bg-black max-h-[60vh] ${isAudio ? 'hidden' : ''}`}
        />
        {isAudio && (
          <div data-testid="preview-audio-wrap" className="w-full bg-slate-900/60 p-6 flex flex-col items-center gap-3">
            <div className="w-16 h-16 rounded-full gradient-brand flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="currentColor"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zm-7 9a1 1 0 1 0-2 0 9 9 0 0 0 8 8.94V22a1 1 0 1 0 2 0v-2.06A9 9 0 0 0 21 11a1 1 0 1 0-2 0 7 7 0 0 1-14 0z"/></svg>
            </div>
            <div className="text-xs text-slate-400 uppercase tracking-wider">Voice recording</div>
            <audio data-testid="preview-audio" src={r.url} controls autoPlay className="w-full" />
          </div>
        )}
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            {renaming ? (
              <>
                <Input data-testid="preview-title-input" value={title} onChange={e => setTitle(e.target.value)} placeholder={r.title} className="flex-1" />
                <Button data-testid="preview-title-save" size="icon" onClick={saveTitle} className="gradient-brand text-white"><Check className="w-4 h-4" /></Button>
              </>
            ) : (
              <>
                <div data-testid="preview-title" className="font-display text-lg font-bold flex-1 truncate">{r.title}</div>
                <Button size="icon" variant="ghost" onClick={() => { setTitle(r.title); setRenaming(true); }} data-testid="preview-rename"><Edit3 className="w-4 h-4" /></Button>
              </>
            )}
          </div>
          <div className="grid grid-cols-3 text-xs text-slate-400">
            <div><div className="uppercase tracking-wider">Duration</div><div data-testid="preview-duration" className="text-slate-200 font-mono">{formatDuration(r.duration_sec)}</div></div>
            <div><div className="uppercase tracking-wider">Size</div><div data-testid="preview-size" className="text-slate-200">{formatBytes(r.size)}</div></div>
            <div><div className="uppercase tracking-wider">Format</div><div data-testid="preview-format" className="text-slate-200">{ext}</div></div>
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            {allowDownloads && (
              <Button data-testid="preview-download" onClick={download} className="gradient-brand text-white flex-1 min-w-[140px]"><Download className="w-4 h-4 mr-2" /> Download</Button>
            )}
            {allowSharing && (
              <Button data-testid="preview-share" variant="outline" onClick={doShare} className="flex-1 min-w-[120px]"><Share2 className="w-4 h-4 mr-2" /> Share</Button>
            )}
            <Button data-testid="preview-delete" variant="outline" onClick={doDelete} className="text-red-400 border-red-500/30 hover:bg-red-500/10"><Trash2 className="w-4 h-4 mr-2" /> Delete</Button>
          </div>
          <p className="text-[11px] text-slate-500 text-center pt-1">
            Saved locally on this device • Find it later in Profile → My Recordings
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
